import React, { useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  DisablableOption,
  FloatingFeature,
} from "@models";
import { 
  CHAR_FEATURE_TYPES
} from "@dndModels/Constants";
import { FeatureType } from '../models/Types';

import API from "@utilities/smart_api";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox"; 
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  name?: string;
  limit?: number;
  feature_type?: FeatureType | "Any";
  allowed_feature_types?: FeatureType[];
  floating_feature_id: string;
  exclude_ff?: boolean;
  onFloatingFeatureSelected: (floating_feature: FloatingFeature) => void;
}

const api = API.getInstance();

export default function FloatingFeatureBox({
  name = "Selected Floating Feature",
  limit = 50,
  feature_type: props_feature_type = "Any",
  floating_feature_id: props_floating_feature_id,
  exclude_ff = false,
  allowed_feature_types = CHAR_FEATURE_TYPES,
  onFloatingFeatureSelected
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ffs, setFFs] = useState<FloatingFeature[]>([]);
  const [limit_msg, setLimitMsg] = useState("");
  const [options, setOptions] = useState<DisablableOption[]>([]);
  const [selected_floating_feature, setSelectedFloatingFeature] = useState<FloatingFeature | null>(null);
  const [feature_type, setFeatureType] = useState(props_feature_type);
  const [search_string, setSearchString] = useState("");

  const load = useCallback(async () => {
    // debugger;
    const new_ffs = await api.getObjects<FloatingFeature>({ data_type: "floating_feature" });
    if (allowed_feature_types.includes("Creature Ability")) {
      const new_cfs = await api.getObjects<FloatingFeature>({ data_type: "creature_feature" });
      new_ffs.push(...new_cfs);
    }
    if (props_floating_feature_id !== "") {
      const selected_ff = new_ffs.find(o => o._id === props_floating_feature_id);
      if (selected_ff) {
        setFeatureType(selected_ff.feature_type);
        setSearchString(selected_ff.name);
        if (!exclude_ff) {
          setSelectedFloatingFeature(selected_ff);
        }
        updateOptions(new_ffs, selected_ff.name, selected_ff.feature_type);
      }
    } else {
      updateOptions(new_ffs, "", "Any");
    }
    setFFs(new_ffs);
    setLoading(false);
  }, [props_floating_feature_id, allowed_feature_types, exclude_ff]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  const get_filter = (use_search_str: string, use_feature_type: FeatureType | "Any") => {
    const new_filter: any = {};

    if (use_feature_type !== "Any") {
      new_filter.feature_type = use_feature_type;
    } else if (allowed_feature_types.length !== CHAR_FEATURE_TYPES.length) {
      new_filter.feature_type = { "in": allowed_feature_types };
    }
    if (use_search_str !== "") {
      const pieces = use_search_str.split("|");
      new_filter["and"] = pieces.map(o => {
        return { "or": [{ "name*includes": o }, { "description*includes": o }]};
      });
    }

    return new_filter;
  }

  const updateOptions = (use_ffs: FloatingFeature[], use_search_str: string, use_feature_type: FeatureType | "Any") => {
    const filtered = api.filter("floating_feature", use_ffs, get_filter(use_search_str, use_feature_type)) as FloatingFeature[];
    if (filtered.length > limit) {
      setLimitMsg(`Too many options: ${filtered.length}`);
    } else {
      setLimitMsg("");
      setOptions(makeOptions(filtered));
    }
  }

  const makeOptions = (use_ffs: FloatingFeature[]) => {
    return use_ffs.map(ff => {
      const opt: DisablableOption = {
        id: ff._id, text: `${ff.name} (${ff.owner_id !== "" && ff.owner_name.length > 0 ? "By " + ff.owner_name + " " : ""}${ff.description})`
      };
      return opt;
    });
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        { props_feature_type === "Any" &&
          <Grid size={6}>
            <SelectStringBox 
              options={["Any",...allowed_feature_types.sort((a,b) => { return a.localeCompare(b); })]}
              value={feature_type} 
              name="Feature Type"
              onChange={(changed: FeatureType | "Any") => {
                setFeatureType(changed);
                updateOptions(ffs, search_string, changed);
              }}
            />
          </Grid>
        }
        <Grid
          size={{
            xs: props_feature_type === "Any" ? 6 : 12
          }}>
          <StringBox 
            value={search_string} 
            name="Search"
            onBlur={(changed: string) => {
              setSearchString(changed);
              updateOptions(ffs, changed, feature_type);
            }}
          />
        </Grid>
        <Grid size={12}>
          { limit_msg.length === 0 ?
            <SelectStringBox
              name={name}
              options={options}
              value={ selected_floating_feature ? selected_floating_feature._id : "" }
              auto_sort
              onChange={(changed: string) => {
                let new_selected = api.get_floating_feature(changed);
                if (!new_selected) {
                  new_selected = api.get_creature_feature(changed);
                }
                if (new_selected) {
                  onFloatingFeatureSelected(new_selected);
                }
                setSelectedFloatingFeature(new_selected);
              }}
            />
          :
            <span className="red centered">{ limit_msg }</span>
          }
        </Grid>
      </Grid>
    );
  }
}